/**
 * BulkDeleteProductsDialog — the confirm → escalate → report flow behind the
 * Products page's bulk Delete action (`inventory.deleteProductsGlobal`).
 *
 * WHY A THREE-STAGE DIALOG rather than confirm-then-toast: the backend is
 * continue-on-failure and refuses per product for five distinct reasons, so a
 * single toast would either hide which products survived or dump raw English
 * reasons on a zh operator. Each stage owns one decision:
 *
 *   1. 'confirm'  — "N products, M variants, cannot be undone" + the
 *                   print-routing disclosure note.
 *   2. 'stock'    — products refused for the FORCEABLE reasons (`has_stock`,
 *                   `has_projections`). Lists stocked variants (sku / on hand
 *                   / committed) and projection counts, then re-sends ONLY
 *                   those ids with `force: true` + `forceAcknowledgedSkus`
 *                   echoing every displayed stocked sku. A forced pass CAN
 *                   land back here — new stock since the last disclosure is
 *                   refused server-side and re-listed for re-acknowledgement.
 *                   `pap_variants` / `blocked` rows are NEVER force-retried;
 *                   forcing would not change the backend's answer.
 *   3. 'summary'  — persistent, per-reason report that survives until the
 *                   operator dismisses it (also held open on a clean delete
 *                   whose print-routing teardown was incomplete). Reasons are
 *                   localized from `REASON_I18N`; the backend's English
 *                   `detail` string only ever renders as secondary muted text.
 *
 * The target list is SNAPSHOT when the dialog opens: every pass invalidates the
 * products query, which prunes the caller's selection, which would otherwise
 * mutate the list the operator is reading mid-flow.
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { toast } from 'sonner';

type DeleteResult = RouterOutputs['inventory']['deleteProductsGlobal']['results'][number];
type FailureReason = NonNullable<DeleteResult['reason']> | 'unknown';

/** Schema cap on `deleteProductsGlobalSchema.productIds` (max 50). The page
 *  itself caps a selection at one 50-row page today, so the loop normally runs
 *  once — it exists so a larger page size can never silently 400. */
const DELETE_CHUNK = 50;

/** Report order: actionable-by-the-operator first, stale data last. */
const FAILURE_REASON_ORDER: FailureReason[] = [
  'pap_variants',
  'has_stock',
  'has_projections',
  'blocked',
  'not_found',
  'unknown',
];

/** Refusals the escalation stage can force past (everything else is final). */
const FORCEABLE_REASONS = new Set<FailureReason>(['has_stock', 'has_projections']);
const isForceable = (r: DeleteResult) => !r.ok && FORCEABLE_REASONS.has(reasonKey(r.reason));

/** Reason → localized copy. Structural (not inline `t()` calls) so a new
 *  backend reason cannot ship as raw English: unmapped values fall to
 *  'unknown'. */
const REASON_I18N: Record<FailureReason, { label: string; hint?: string }> = {
  pap_variants: {
    label: 'products.bulkDelete.reasonPapVariants',
    hint: 'products.bulkDelete.reasonPapVariantsHint',
  },
  has_stock: {
    label: 'products.bulkDelete.reasonHasStock',
    hint: 'products.bulkDelete.reasonHasStockHint',
  },
  has_projections: {
    label: 'products.bulkDelete.reasonHasProjections',
    hint: 'products.bulkDelete.reasonHasProjectionsHint',
  },
  blocked: {
    label: 'products.bulkDelete.reasonBlocked',
    hint: 'products.bulkDelete.reasonBlockedHint',
  },
  not_found: {
    label: 'products.bulkDelete.reasonNotFound',
    hint: 'products.bulkDelete.reasonNotFoundHint',
  },
  unknown: { label: 'products.bulkDelete.reasonUnknown' },
};

const reasonKey = (reason: DeleteResult['reason']): FailureReason =>
  reason && reason in REASON_I18N ? reason : 'unknown';

export type BulkDeleteProductTarget = {
  id: string;
  title: string | null;
  variantCount: number;
};

type Props = {
  open: boolean;
  /** Dismiss. Never called while a pass is in flight. */
  onClose: () => void;
  /** Selected products. Snapshot on open, so later mutations of this array
   *  (selection pruning after an invalidate) cannot rewrite the flow. */
  products: BulkDeleteProductTarget[];
  /** Every target deleted: clear the selection AND refresh. */
  onDeleted: () => void | Promise<void>;
  /** Some deleted / some refused: refresh WITHOUT clearing the selection, so
   *  the operator keeps the refused products selected to act on them. */
  onPartialFailure: () => void | Promise<void>;
};

type Stage = 'confirm' | 'stock' | 'summary';

export default function BulkDeleteProductsDialog({
  open,
  onClose,
  products,
  onDeleted,
  onPartialFailure,
}: Props) {
  const { t } = useTranslation('inventory');
  const mutation = trpc.inventory.deleteProductsGlobal.useMutation();

  const [targets, setTargets] = useState<BulkDeleteProductTarget[]>([]);
  const [stage, setStage] = useState<Stage>('confirm');
  const [stockBlocked, setStockBlocked] = useState<DeleteResult[]>([]);
  const [failures, setFailures] = useState<DeleteResult[]>([]);
  const [totals, setTotals] = useState({ products: 0, variants: 0, projections: 0, teardownIncomplete: 0 });
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Read the live selection through a ref so the open-snapshot effect depends
  // on `open` alone (a `products` dep would re-snapshot mid-flow on every
  // invalidate).
  const productsRef = useRef(products);
  productsRef.current = products;

  // Snapshot + reset BEFORE paint (layout effect, not effect): this component
  // stays mounted between sessions, so a plain effect would paint one frame of
  // the PREVIOUS session's summary as the dialog animates back in. Resetting on
  // close instead would flicker during the exit animation.
  useLayoutEffect(() => {
    if (!open) return;
    setTargets(productsRef.current);
    setStage('confirm');
    setStockBlocked([]);
    setFailures([]);
    setTotals({ products: 0, variants: 0, projections: 0, teardownIncomplete: 0 });
    setFatalError(null);
  }, [open]);

  // Deletes are irreversible and the loop is not resumable — warn on unload
  // only while a pass is actually in flight.
  useEffect(() => {
    if (!submitting) return;
    const warning = t('products.bulkDelete.unloadWarning');
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = warning;
      return warning;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [submitting, t]);

  const targetVariantCount = useMemo(
    () => targets.reduce((sum, p) => sum + p.variantCount, 0),
    [targets],
  );

  const groupedFailures = useMemo(() => {
    const byReason = new Map<FailureReason, DeleteResult[]>();
    for (const failure of failures) {
      const key = reasonKey(failure.reason);
      const existing = byReason.get(key);
      if (existing) existing.push(failure);
      else byReason.set(key, [failure]);
    }
    return FAILURE_REASON_ORDER.filter((r) => byReason.has(r)).map((reason) => ({
      reason,
      items: byReason.get(reason) ?? [],
    }));
  }, [failures]);

  const countLabels = (productCount: number, variantCount: number) => ({
    products: t('products.bulkBar.productCount', { count: productCount }),
    variants: t('products.bulkBar.variantCount', { count: variantCount }),
  });

  const runPass = async (ids: string[], force: boolean) => {
    if (ids.length === 0) return;
    setFatalError(null);
    setSubmitting(true);

    // BLU-759: `force` alone is refused server-side for stocked variants —
    // the request must echo every stocked sku it is forcing. The escalation
    // stage displayed exactly these skus, so acknowledging them here is the
    // "I read the table" signal the backend requires.
    const forceAcknowledgedSkus = force
      ? [...new Set(stockBlocked.flatMap((r) => (r.stockedVariants ?? []).map((v) => v.sku)))]
      : [];

    const collected: DeleteResult[] = [];
    let deletedProducts = 0;
    let deletedVariants = 0;
    let removedProjections = 0;
    let thrownMessage: string | null = null;

    try {
      for (let i = 0; i < ids.length; i += DELETE_CHUNK) {
        const result = await mutation.mutateAsync({
          productIds: ids.slice(i, i + DELETE_CHUNK),
          // Omitted (not `false`) on the first pass — `force` exists only as
          // the escalation-stage retry.
          ...(force
            ? {
                force: true,
                ...(forceAcknowledgedSkus.length > 0 ? { forceAcknowledgedSkus } : {}),
              }
            : {}),
        });
        collected.push(...result.results);
        deletedProducts += result.deletedProducts;
        deletedVariants += result.deletedVariants;
        removedProjections += result.removedProjections ?? 0;
      }
    } catch (err) {
      // `null` means "did not throw"; '' means "threw with no message" — the
      // banner must still render for a messageless failure.
      thrownMessage = err instanceof Error && err.message ? err.message : '';
    } finally {
      setSubmitting(false);
    }

    if (thrownMessage !== null && collected.length === 0) {
      // Nothing landed server-side: leave every piece of state untouched so the
      // same confirm button is a straight retry.
      setFatalError(thrownMessage);
      return;
    }

    const nextTotals = {
      products: totals.products + deletedProducts,
      variants: totals.variants + deletedVariants,
      projections: totals.projections + removedProjections,
      teardownIncomplete:
        totals.teardownIncomplete + collected.filter((r) => r.ok && r.teardownIncomplete).length,
    };
    setTotals(nextTotals);

    // Forceable refusals from ANY pass stay in the escalation set — a forced
    // pass can legitimately come back `has_stock` when NEW stock appeared
    // after the last disclosure (the server refuses unacknowledged skus).
    // The refreshed result carries the new numbers, so the operator reviews
    // again and the next forced pass acknowledges them.
    const passFailures = collected.filter((r) => !r.ok && !isForceable(r));
    const attempted = new Set(collected.map((r) => r.productId));
    // On a forced pass, also KEEP any force-blocked product the loop never
    // reached (a mid-loop throw on a multi-chunk selection) — dropping it
    // would erase the only record that it was still pending.
    const nextStockBlocked = [
      ...(force ? stockBlocked.filter((r) => !attempted.has(r.productId)) : []),
      ...collected.filter(isForceable),
    ];
    // Failures accumulate across passes: pass 1's non-stock refusals must
    // survive the stock escalation, or forcing would silently bury them.
    const nextFailures = force ? [...failures, ...passFailures] : passFailures;

    setStockBlocked(nextStockBlocked);
    setFailures(nextFailures);

    if (thrownMessage !== null) {
      setFatalError(thrownMessage);
      setStage('summary');
      await onPartialFailure();
      return;
    }

    if (nextStockBlocked.length === 0 && nextFailures.length === 0) {
      // An incomplete teardown means stale print-routing rows may still
      // resolve for deleted SKUs — that is NOT a clean success; hold the
      // persistent summary open instead of toast-and-close.
      if (nextTotals.teardownIncomplete > 0) {
        setStage('summary');
        await onDeleted();
        return;
      }
      // The projection teardown must be disclosed even on the all-clear path —
      // the summary stage only renders on partial failure, so the toast is the
      // only surface left to say it.
      toast.success(
        t('products.bulkDelete.successToast', countLabels(nextTotals.products, nextTotals.variants)),
        nextTotals.projections > 0
          ? { description: t('products.bulkDelete.summaryProjections', { count: nextTotals.projections }) }
          : undefined,
      );
      await onDeleted();
      onClose();
      return;
    }

    setStage(nextStockBlocked.length > 0 ? 'stock' : 'summary');
    await onPartialFailure();
  };

  // Reason-aware escalation copy: a product can be force-blocked for stock,
  // for print routing, or both — headlining "still holds stock" over a
  // routing-only refusal would put the wrong reason on an irreversible
  // confirm.
  const hasStockRefusals = stockBlocked.some((r) => (r.stockedVariants ?? []).length > 0);
  const hasProjectionRefusals = stockBlocked.some((r) => (r.projectionCount ?? 0) > 0);

  const stageTitle = (() => {
    if (stage === 'stock') {
      if (hasStockRefusals && hasProjectionRefusals)
        return t('products.bulkDelete.forceTitle', { count: stockBlocked.length });
      if (!hasStockRefusals && hasProjectionRefusals)
        return t('products.bulkDelete.projTitle', { count: stockBlocked.length });
      return t('products.bulkDelete.stockTitle', { count: stockBlocked.length });
    }
    if (stage === 'summary') {
      return totals.products > 0
        ? t('products.bulkDelete.summaryTitle')
        : t('products.bulkDelete.summaryTitleNone');
    }
    return t('products.bulkDelete.title', { count: targets.length });
  })();

  const deletedLine =
    totals.products > 0
      ? t('products.bulkDelete.summaryDeleted', countLabels(totals.products, totals.variants))
      : t('products.bulkDelete.summaryNoneDeleted');

  const srStatus = submitting
    ? t('products.bulkDelete.srProgress', { count: targets.length })
    : `${stageTitle} ${stage === 'confirm' ? '' : deletedLine}`.trim();

  const failureList =
    groupedFailures.length > 0 ? (
      <div className="space-y-3">
        {stage === 'stock' && (
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('products.bulkDelete.otherFailures')}
          </p>
        )}
        {groupedFailures.map(({ reason, items }) => {
          const { label, hint } = REASON_I18N[reason];
          return (
            <div key={reason} className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t(label)}
              </p>
              <ul className="space-y-1">
                {items.map((item) => (
                  <li key={item.productId} className="text-sm">
                    {item.title || t('products.untitledProduct')}
                    {/* Backend `detail` is untranslated English — secondary only,
                        never the primary explanation. */}
                    {item.detail && (
                      <span className="block text-xs text-muted-foreground">{item.detail}</span>
                    )}
                  </li>
                ))}
              </ul>
              {hint && <p className="text-xs text-muted-foreground">{t(hint)}</p>}
            </div>
          );
        })}
      </div>
    ) : null;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submitting) onClose();
      }}
    >
      <AlertDialogContent
        className="sm:max-w-xl"
        onEscapeKeyDown={(e) => {
          if (submitting) e.preventDefault();
        }}
      >
        <span role="status" aria-live="polite" className="sr-only">
          {srStatus}
        </span>

        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {stage !== 'confirm' && (
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            )}
            {stageTitle}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {stage === 'confirm' &&
              t('products.bulkDelete.description', countLabels(targets.length, targetVariantCount))}
            {stage === 'stock' &&
              (hasStockRefusals
                ? t('products.bulkDelete.stockWarning')
                : t('products.bulkDelete.projWarning'))}
            {stage === 'stock' && hasStockRefusals && hasProjectionRefusals && (
              <span className="block">{t('products.bulkDelete.projWarning')}</span>
            )}
            {stage === 'summary' && deletedLine}
            {stage === 'summary' && totals.projections > 0 && (
              <span className="block">
                {t('products.bulkDelete.summaryProjections', { count: totals.projections })}
              </span>
            )}
            {stage === 'summary' && totals.teardownIncomplete > 0 && (
              <span className="block text-amber-700 dark:text-amber-500">
                {t('products.bulkDelete.teardownWarning', { count: totals.teardownIncomplete })}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {stage === 'confirm' && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t('products.bulkDelete.scopeNote')}</p>
            <p className="text-xs text-muted-foreground">{t('products.bulkDelete.projectionNote')}</p>
          </div>
        )}

        {stage !== 'confirm' && (
          <div className="max-h-[55vh] space-y-4 overflow-y-auto">
            {stage === 'stock' && (
              <div className="rounded-md border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-background">
                    <tr className="[&>th]:px-2.5 [&>th]:py-1.5 [&>th]:font-normal [&>th]:text-muted-foreground">
                      <th className="text-left">{t('table.sku')}</th>
                      <th className="w-24 text-right">{t('table.onHandShort')}</th>
                      <th className="w-24 text-right">{t('table.committedShort')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockBlocked.map((product) => (
                      <Fragment key={product.productId}>
                        <tr className="bg-muted/30">
                          <td colSpan={3} className="px-2.5 py-1.5 text-sm font-medium">
                            {product.title || t('products.untitledProduct')}
                          </td>
                        </tr>
                        {(product.stockedVariants ?? []).map((variant) => (
                          <tr key={`${product.productId}-${variant.sku}`} className="border-t">
                            <td className="px-2.5 py-1.5 font-mono">{variant.sku}</td>
                            <td className="px-2.5 py-1.5 text-right tabular-nums">
                              {variant.currentStock}
                            </td>
                            <td className="px-2.5 py-1.5 text-right tabular-nums">
                              {variant.committed}
                            </td>
                          </tr>
                        ))}
                        {(product.projectionCount ?? 0) > 0 && (
                          <tr className="border-t">
                            <td colSpan={3} className="px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-500">
                              {t('products.bulkDelete.projectionLine', {
                                count: product.projectionCount ?? 0,
                              })}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {failureList}
          </div>
        )}

        {fatalError !== null && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3" role="alert">
            <p className="text-sm text-destructive">{t('products.bulkDelete.fatalError')}</p>
            {/* Transport/server message: untranslated, so muted secondary only. */}
            {fatalError !== '' && (
              <p className="mt-1 text-xs text-muted-foreground">{fatalError}</p>
            )}
          </div>
        )}

        <AlertDialogFooter>
          {stage === 'summary' ? (
            <AlertDialogCancel>{t('common.close')}</AlertDialogCancel>
          ) : (
            <AlertDialogCancel disabled={submitting}>
              {stage === 'stock' ? t('products.bulkDelete.stockKeep') : t('common.cancel')}
            </AlertDialogCancel>
          )}

          {stage === 'confirm' && (
            <Button
              type="button"
              variant="destructive"
              className="gap-1.5"
              disabled={submitting || targets.length === 0}
              onClick={() => void runPass(targets.map((p) => p.id), false)}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              )}
              {submitting ? t('products.bulkDelete.deleting') : t('products.bulkDelete.confirm')}
            </Button>
          )}

          {stage === 'stock' && (
            <Button
              type="button"
              variant="destructive"
              className="gap-1.5"
              disabled={submitting}
              onClick={() => void runPass(stockBlocked.map((r) => r.productId), true)}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              )}
              {submitting ? t('products.bulkDelete.deleting') : t('products.bulkDelete.stockConfirm')}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
